import React, { Component, ReactNode } from 'react';
import ComponentName from '../constant/ComponentName';
import Icon from '../icons/Icon';
import { FileUpload, FileUploadHandlerParam } from 'primereact/fileupload';
import RoleHelper from '../RoleHelper';
import CustomButton from './custom/CustomButton';
import ComponentService from '../service/ComponentService';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import MessageUtil from '../util/MessageUtil';
import { RootState } from '../redux/store';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import LanguageUtil from '../util/LanguageUtil';
import { ImportErrorReason } from 'redux/reducers/ImportReasonReducer';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import EnvironmentUtil from '../util/EnvironmentUtil';

class ComponentImportComponent extends Component<Props, State> {
  public state: State = new State();

  private componentService: ComponentService = ServiceContainer.getInstance().get(ServiceNames.COMPONENT);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render(): ReactNode {
    const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, className: 'custom-choose-btn p-button-rounded p-button-outlined' };
    return (
      <div className={'w-full h-full'}>
        <div className={'w-24 inline'}>
          <div className={'text-center mb-4'}>
            <Icon icon={'csv-file'} size={50} />
          </div>
          <div className={'grid text-center'}>
            <span className={'font-medium text-xs'}>{LanguageUtil.get({ id: 'common.label.uploadJson', defaultMessage: 'Upload JSON File' })}</span>
          </div>
        </div>
        <FileUpload
          className={'mt-4'}
          emptyTemplate={this.emptyTemplate}
          itemTemplate={this.itemTemplate}
          multiple={false}
          name={'file'}
          auto
          chooseLabel={LanguageUtil.get({ id: 'common.label.browse', defaultMessage: 'Browse' })}
          maxFileSize={this.environmentUtil.getStorageMaxSize()}
          accept={'.json'}
          customUpload
          uploadHandler={this.fileSelect}
          chooseOptions={chooseOptions}
        />
      </div>
    );
  }

  private fileSelect = (e: FileUploadHandlerParam) => {
    const { component } = this.props;
    if (!RoleHelper.canEdit(component)) return;
    this.setState({ file: e.files[0] });
  };

  private emptyTemplate = () => {
    return (
      <div className='text-center'>
        <ul>
          <li>
            <Icon icon='group-186' size={100} />
          </li>
          <li>
            <span className={'font-medium text-xs'}>{LanguageUtil.get({ id: 'common.label.drag', defaultMessage: 'Drag & Drop file here' })}</span>
          </li>
          <li>
            <span className={'font-normal text-xs'}>{LanguageUtil.get({ id: 'common.label.or', defaultMessage: 'or' })}</span>
          </li>
          <li>
            <span className={'font-light text-2xs'}>{LanguageUtil.get({ id: 'common.label.selectFile', defaultMessage: 'You can select file by' })}</span>
          </li>
        </ul>
      </div>
    );
  };

  private itemTemplate = () => {
    return (
      <div className='flex justify-center'>
        <CustomButton text={LanguageUtil.get({ id: 'common.label.upload', defaultMessage: 'Action' })} onClick={this.import} />
      </div>
    );
  };

  private import = async () => {
    const { component, close, page } = this.props;
    const { file } = this.state;
    if (file == undefined) return;
    const processedFile = await this.processFile(file);
    const category = page.currentCategory as EnrichedComponentCategory | undefined;
    const result: ImportErrorResponse = await this.componentService.import(component, processedFile, category?.id);
    this.logImportResult(result);
    const keys = Object.keys(result);
    if (keys?.length === 0) {
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.import' }),
        detail: LanguageUtil.get({ id: 'messages.success.modelImport', defaultMessage: 'Model import successful.' }),
      });
    } else {
      const entries = Object.entries(result);
      const alreadyExistKeys = entries.filter(([, v]) => v?.reason === 'ALREADY_EXIST').map(([k]) => k);
      const otherFailedKeys = entries.filter(([, v]) => v?.reason !== 'ALREADY_EXIST').map(([k]) => k);

      if (alreadyExistKeys.length > 0) {
        const names = alreadyExistKeys.join(', ');
        const isMultiple = alreadyExistKeys.length > 1;
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'common.label.import' }),
          detail: LanguageUtil.get(
            {
              id: isMultiple ? 'messages.warning.importAlreadyExistMultiple' : 'messages.warning.importAlreadyExistSingle',
              defaultMessage: isMultiple ? `These models already exist: ${names}` : `This model already exists: ${names}`,
            },
            { 0: names },
          ),
        });
      }

      if (otherFailedKeys.length > 0) {
        const names = otherFailedKeys.join(', ');
        const isMultiple = otherFailedKeys.length > 1;
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'common.label.import' }),
          detail: LanguageUtil.get(
            {
              id: isMultiple ? 'messages.warning.importMultiple' : 'messages.warning.importSingle',
              defaultMessage: isMultiple ? `These models could not be imported: ${names}` : `This model could not be imported: ${names}`,
            },
            { 0: names },
          ),
        });
      }

      return;
    }
    close();
  };

  private logImportResult(result: ImportErrorResponse) {
    const failedItemKeys = Object.keys(result);
    if (failedItemKeys.length === 0) {
      return;
    }
    for (const failedItemKey of failedItemKeys) {
      const failure = result[failedItemKey];
      console.error('[component-import] failed item detail', {
        itemId: failedItemKey,
        reason: failure?.reason,
        payload: failure?.payload,
      });
    }
  }

  private processFile = async (file: File): Promise<File> => {
    const text = await file.text();
    const json = JSON.parse(text);
    const now = Date.now();

    const normalize = (item: any) => ({ ...item, createdAt: item.createdAt ?? now });
    const processed = Array.isArray(json) ? json.map(normalize) : normalize(json);

    return new File([JSON.stringify(processed, null, 2)], file.name, {
      type: 'application/json',
    });
  };
}

interface Props extends ReduxProps {
  component: ComponentName;
  close: () => void;
}

class State {
  file: File | undefined;
}

export type ImportErrorResponse = Record<
  string,
  {
    reason: ImportErrorReason;
    payload: string | Record<string, { id: string; args: Record<string, any> }[]>;
  }
>;

const mapStateToProps = (state: RootState) => ({
  page: state.page,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ComponentImportComponent);
